/**
 * State hashing — the instrument every determinism guarantee is read through.
 *
 * The scope of this hash is the **whole simulation**, not the entity buffers.
 * An under-scoped hash weakens determinism (R1), replays (R5), desync detection
 * (R29), and both acceptance examples that rest on them, simultaneously and
 * silently: the gate reports healthy while the game diverges.
 *
 * So every unit that adds simulation state calls `register`, and a test
 * enumerates the registered regions and fails on any that does not move the
 * hash. See ARCHITECTURE.md §7 for the region table.
 */

const FNV_OFFSET = 0x811c9dc5
const FNV_PRIME = 0x01000193

/** Anything with a byte range: every typed array satisfies this. */
export interface ByteRegion {
  readonly buffer: ArrayBufferLike
  readonly byteOffset: number
  readonly byteLength: number
}

export type RegionProvider = ByteRegion | (() => ByteRegion)

interface Region {
  name: string
  provider: RegionProvider
}

export class HashRegistry {
  private readonly regions: Region[] = []
  private scratch: Uint8Array | null = null

  /**
   * Register a region of simulation state.
   *
   * Registration order is part of the hash, and it is deterministic because
   * subsystem init order is. Pass a function for state whose backing buffer is
   * reallocated (a growable array); pass the view directly otherwise.
   */
  register(name: string, provider: RegionProvider): void {
    if (this.regions.some((r) => r.name === name)) {
      throw new Error(`hash region '${name}' registered twice`)
    }
    this.regions.push({ name, provider })
  }

  /** Registered region names, in hash order. Used by the enumeration test. */
  names(): string[] {
    return this.regions.map((r) => r.name)
  }

  clear(): void {
    this.regions.length = 0
  }

  /** FNV-1a over every registered region, in registration order. */
  compute(): number {
    let h = FNV_OFFSET
    for (let i = 0; i < this.regions.length; i++) {
      const region = this.resolve(i)
      h = fnv1aBytes(region, h)
      // Fold the byte length in too, so a region that shrinks by exactly a run
      // of zero bytes still moves the hash.
      h = fnv1aWord(region.byteLength, h)
    }
    return h >>> 0
  }

  /** Per-region hashes, for narrowing a desync to a subsystem after the fact. */
  breakdown(): Array<{ name: string; hash: number }> {
    const out: Array<{ name: string; hash: number }> = []
    for (let i = 0; i < this.regions.length; i++) {
      const bytes = this.resolve(i)
      out.push({ name: this.regions[i].name, hash: fnv1aBytes(bytes, FNV_OFFSET) >>> 0 })
    }
    return out
  }

  private resolve(i: number): Uint8Array {
    const p = this.regions[i].provider
    const region = typeof p === 'function' ? p() : p
    // A fresh Uint8Array view here allocates, but `compute` runs once every 30
    // ticks, not per tick, so this is off the hot path by construction.
    return new Uint8Array(region.buffer, region.byteOffset, region.byteLength)
  }

  /** Scratch space for callers that need to hash a value list without allocating. */
  scratchBytes(n: number): Uint8Array {
    if (!this.scratch || this.scratch.length < n) this.scratch = new Uint8Array(n)
    return this.scratch.subarray(0, n)
  }
}

/** FNV-1a over a byte range, chainable via `seed`. */
export function fnv1aBytes(bytes: Uint8Array, seed = FNV_OFFSET): number {
  let h = seed
  for (let i = 0; i < bytes.length; i++) {
    h ^= bytes[i]
    h = Math.imul(h, FNV_PRIME)
  }
  return h >>> 0
}

/** FNV-1a over the four bytes of a 32-bit word, little-endian. */
export function fnv1aWord(v: number, seed = FNV_OFFSET): number {
  let h = seed
  h = Math.imul(h ^ (v & 0xff), FNV_PRIME)
  h = Math.imul(h ^ ((v >>> 8) & 0xff), FNV_PRIME)
  h = Math.imul(h ^ ((v >>> 16) & 0xff), FNV_PRIME)
  h = Math.imul(h ^ ((v >>> 24) & 0xff), FNV_PRIME)
  return h >>> 0
}

/** FNV-1a over a string. Used for stable ids, never for simulation state. */
export function fnv1aString(s: string, seed = FNV_OFFSET): number {
  let h = seed
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i) & 0xff
    h = Math.imul(h, FNV_PRIME)
    h ^= (s.charCodeAt(i) >>> 8) & 0xff
    h = Math.imul(h, FNV_PRIME)
  }
  return h >>> 0
}

/** Zero-padded lowercase hex, so hash traces diff cleanly in a terminal. */
export function hashHex(h: number): string {
  return (h >>> 0).toString(16).padStart(8, '0')
}
